import React from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight, Layers, Sparkles, Hammer, ArrowRight } from "lucide-react";
import { useI18n } from "@/i18n/I18nProvider";
import IMG from "@/constants/images";

export default function HomePage() {
  const { t } = useI18n();

  return (
    <div data-testid="page-home">
      {/* HERO */}
      <section className="relative min-h-[92vh] flex items-end overflow-hidden">
        <div className="absolute inset-0">
          <div
            className="absolute inset-0 kenburns bg-cover bg-center"
            style={{ backgroundImage: `url(${IMG.hero})` }}
          />
          <div className="absolute inset-0 bg-gradient-to-b from-[hsl(var(--ink))]/30 via-[hsl(var(--ink))]/10 to-[hsl(var(--bg))]" />
        </div>

        <div className="container-x relative pb-16 md:pb-24 pt-32 w-full">
          <div className="max-w-4xl reveal">
            <div className="eyebrow text-[hsl(var(--bg))] mix-blend-difference">
              {t.home.eyebrow}
            </div>
            <h1
              className="mt-6 text-[clamp(2.6rem,7vw,6.25rem)] leading-[0.95] tracking-[-0.02em] text-[hsl(var(--bg))]"
              style={{ fontFamily: "'Playfair Display', serif", fontWeight: 500 }}
            >
              {t.home.titleA}<br />
              <span className="italic font-normal text-[hsl(var(--ocre))]">{t.home.titleB}</span>
            </h1>
            <p className="mt-7 max-w-xl text-base md:text-lg text-[hsl(var(--bg))]/85 leading-relaxed">
              {t.home.lead}
            </p>
            <div className="mt-10 flex flex-wrap gap-4">
              <Link to="/contact" className="btn-primary" data-testid="hero-cta-quote">
                {t.home.ctaPrimary} <ArrowUpRight className="h-4 w-4" />
              </Link>
              <Link
                to="/services"
                data-testid="hero-cta-services"
                className="inline-flex items-center gap-2 px-6 py-3.5 text-sm uppercase tracking-[0.18em] font-medium border border-[hsl(var(--bg))]/40 text-[hsl(var(--bg))] hover:bg-[hsl(var(--bg))] hover:text-[hsl(var(--ink))] transition-all duration-300"
              >
                {t.home.ctaGhost}
              </Link>
            </div>
          </div>
        </div>

        {/* corner marks */}
        <div className="absolute bottom-6 right-6 md:right-10 text-[hsl(var(--bg))]/70 text-[10px] uppercase tracking-[0.3em]">
          {t.common.based}
        </div>
      </section>

      {/* INTRO */}
      <section className="container-x py-24 md:py-32">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-10 md:gap-16 items-start">
          <div className="md:col-span-4">
            <div className="eyebrow">— 01 / Atelier</div>
            <h2 className="mt-6 text-3xl md:text-4xl lg:text-5xl leading-[1.05] tracking-[-0.01em]">
              {t.home.introTitle}
            </h2>
          </div>
          <div className="md:col-span-7 md:col-start-6">
            <p className="text-lg leading-relaxed text-[hsl(var(--ink-soft))]">
              {t.home.introBody}
            </p>
            <div className="mt-10 grid grid-cols-3 gap-6 border-t border-[hsl(var(--line))] pt-8">
              <div>
                <div className="stat-num text-3xl md:text-4xl">{t.home.stat1}</div>
                <div className="text-xs uppercase tracking-[0.18em] text-[hsl(var(--muted-foreground))] mt-2">{t.home.stat1Label}</div>
              </div>
              <div>
                <div className="stat-num text-3xl md:text-4xl">{t.home.stat2}</div>
                <div className="text-xs uppercase tracking-[0.18em] text-[hsl(var(--muted-foreground))] mt-2">{t.home.stat2Label}</div>
              </div>
              <div>
                <div className="stat-num text-3xl md:text-4xl">{t.home.stat3}</div>
                <div className="text-xs uppercase tracking-[0.18em] text-[hsl(var(--muted-foreground))] mt-2">{t.home.stat3Label}</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* SERVICES OVERVIEW */}
      <section className="bg-[hsl(var(--bg-elev))] py-24 md:py-32" data-testid="home-services">
        <div className="container-x">
          <div className="flex items-end justify-between flex-wrap gap-6 mb-14">
            <div className="max-w-2xl">
              <div className="eyebrow">— 02 / {t.home.servicesEyebrow}</div>
              <h2 className="mt-5 text-3xl md:text-5xl leading-[1.05]">
                {t.home.servicesTitle}
              </h2>
            </div>
            <Link to="/services" className="hidden md:inline-flex items-center gap-2 text-sm uppercase tracking-[0.18em] link-underline" data-testid="home-services-all">
              {t.common.readMore} <ArrowRight className="h-4 w-4" />
            </Link>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-px bg-[hsl(var(--line))] border border-[hsl(var(--line))]">
            <ServiceCard
              icon={<Layers className="h-6 w-6" />}
              title={t.home.service1Title}
              body={t.home.service1Body}
              image={IMG.servicePlaster}
              num="01"
            />
            <ServiceCard
              icon={<Sparkles className="h-6 w-6" />}
              title={t.home.service2Title}
              body={t.home.service2Body}
              image={IMG.serviceStucco}
              num="02"
              featured
            />
            <ServiceCard
              icon={<Hammer className="h-6 w-6" />}
              title={t.home.service3Title}
              body={t.home.service3Body}
              image={IMG.serviceRestoration}
              num="03"
            />
          </div>
        </div>
      </section>

      {/* PROCESS */}
      <section className="container-x py-24 md:py-32">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-10">
          <div className="md:col-span-5">
            <div className="eyebrow">— 03 / {t.home.processEyebrow}</div>
            <h2 className="mt-6 text-3xl md:text-5xl leading-[1.05]">
              {t.home.processTitle}
            </h2>
            <div className="mt-10 aspect-[4/5] overflow-hidden">
              <img src={IMG.craftHands} alt="" className="w-full h-full object-cover" />
            </div>
          </div>

          <div className="md:col-span-6 md:col-start-7 md:pt-16">
            {[t.home.step1, t.home.step2, t.home.step3, t.home.step4].map((step, i) => (
              <div
                key={i}
                className="grid grid-cols-[auto_1fr] gap-8 py-7 border-b border-[hsl(var(--line))] first:border-t"
                data-testid={`process-step-${i + 1}`}
              >
                <div className="stat-num text-2xl md:text-3xl text-[hsl(var(--ocre-deep))] w-12">
                  0{i + 1}
                </div>
                <div className="text-xl md:text-2xl leading-snug" style={{ fontFamily: "'Playfair Display', serif" }}>
                  {step}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* QUOTE / TESTIMONIAL */}
      <section className="bg-[hsl(var(--umber))] text-[hsl(var(--bg))] py-24 md:py-32 relative overflow-hidden">
        <div className="container-x relative">
          <div className="max-w-4xl">
            <div className="text-[hsl(var(--ocre))] eyebrow">— Manifesto</div>
            <blockquote
              className="mt-8 text-[clamp(1.8rem,4.5vw,3.75rem)] leading-[1.1] tracking-[-0.01em]"
              style={{ fontFamily: "'Playfair Display', serif", fontWeight: 400 }}
            >
              <span className="text-[hsl(var(--ocre))]">“</span>
              {t.home.quote}
              <span className="text-[hsl(var(--ocre))]">”</span>
            </blockquote>
            <div className="mt-10 text-[hsl(var(--bg))]/70 text-sm uppercase tracking-[0.22em]">
              — {t.home.quoteBy}
            </div>
          </div>
        </div>
        <div className="pointer-events-none absolute -bottom-16 -right-10 opacity-[0.06] display text-[24vw] leading-none">
          ”
        </div>
      </section>

      {/* CTA */}
      <section className="container-x py-24 md:py-32">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-10 items-end">
          <div className="md:col-span-7">
            <h2 className="text-3xl md:text-6xl leading-[1.02] tracking-[-0.015em]">
              {t.home.ctaTitle}
            </h2>
            <p className="mt-6 text-[hsl(var(--ink-soft))] text-lg max-w-xl">
              {t.home.ctaBody}
            </p>
          </div>
          <div className="md:col-span-5 md:text-right">
            <Link to="/contact" className="btn-primary" data-testid="home-final-cta">
              {t.home.ctaPrimary} <ArrowUpRight className="h-4 w-4" />
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}

function ServiceCard({ icon, title, body, image, num, featured = false }) {
  return (
    <div
      className={`group relative bg-[hsl(var(--bg))] p-8 md:p-10 flex flex-col min-h-[460px] overflow-hidden transition-colors duration-500 ${featured ? "md:scale-y-100" : ""}`}
    >
      <div className="flex items-start justify-between mb-10">
        <span className="text-[hsl(var(--ocre-deep))]">{icon}</span>
        <span className="text-[11px] uppercase tracking-[0.22em] text-[hsl(var(--muted-foreground))]">{num}</span>
      </div>
      <h3
        className="text-2xl md:text-3xl leading-snug"
        style={{ fontFamily: "'Playfair Display', serif", fontWeight: 500 }}
      >
        {title}
      </h3>
      <p className="mt-4 text-[hsl(var(--ink-soft))] leading-relaxed text-sm">{body}</p>
      <div className="mt-auto pt-8">
        <div className="aspect-[5/3] overflow-hidden">
          <img
            src={image}
            alt=""
            loading="lazy"
            className="w-full h-full object-cover transition-transform duration-[1.2s] group-hover:scale-105"
          />
        </div>
      </div>
    </div>
  );
}
